import { Alert, Box, Button, LinearProgress, Stack, type Theme, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect } from 'react'
import { ApplicationTypes } from 'sunlife-domain'

import { useLogger } from '@salsita-ai/platform-project-sdk/react'
import { Dialog, PhxIcon } from '@salsita-ai/platform-project-sdk/ui'

import { addVariantToWishlist } from '../../../shopify/shopify-wishlist-client'
import { configuratorInterface } from '../../../systems/configurator-interface'

interface FinalizeWishlistSuccessDialogContentProps {
  finalizationUuid: string
  onClose?: () => void
}

export const FinalizeWishlistSuccessDialogContent = ({
  finalizationUuid,
  onClose,
}: FinalizeWishlistSuccessDialogContentProps) => {
  const { navigationSystem, storeSystem } = configuratorInterface.useDomain()

  const { configurationId } = configuratorInterface.useDomain()
  const apiClient = configuratorInterface.useAuthorizedClient()
  const logger = useLogger()

  const handleBack = useCallback(() => {
    if (onClose) {
      onClose()
    } else {
      storeSystem.stageAndCommit([navigationSystem.getNavigationUpdate(ApplicationTypes.NavigationState.Initial)])
    }
  }, [onClose, navigationSystem, storeSystem])

  const getResult = useCallback(async () => {
    const data = await apiClient.waitForFinalizationToComplete(configurationId, finalizationUuid, {
      pollingTimeout: 20 * 60 * 1000,
    })
    if (!data.shopifyProduct?.productVariantId) {
      logger.error('PRODUCT VARIANT ID NOT FOUND', { data })
      throw new Error('PRODUCT VARIANT ID NOT FOUND')
    }
    await addVariantToWishlist(
      data.shopifyProduct.productVariantId.toString(),
      data.shopifyProduct.productUrl,
      data.shopifyProduct.productId
    )
    // Return a success indicator since addVariantToWishlist returns void
    return { success: true }
  }, [apiClient, configurationId, finalizationUuid, logger])

  const { data, isError, isLoading, error } = useQuery({
    queryKey: ['finalize', finalizationUuid],
    queryFn: getResult,
  })

  // Log error details for debugging
  useEffect(() => {
    if (isError && error) {
      logger.error('Wishlist query error:', {
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
      })
    }
  }, [isError, error, logger])

  return (
    <Dialog.ContentLayout
      controlsPosition="end"
      header={
        <Box
          component="div"
          sx={(theme: Theme) => ({
            backgroundColor: theme.palette.background.default,
            aspectRatio: '2.9',
            display: 'grid',
            placeItems: 'center',
            color: 'green',
          })}
        >
          <PhxIcon name="CheckCircle" style={{ fontSize: 200 }} />
        </Box>
      }
      body={
        <>
          <Stack direction="column" spacing={1} sx={{ pl: 0.5 }}>
            {isError && (
              <Alert severity="error">
                Couldn't add to wishlist. Please try again later or contact support if the issue persists.
              </Alert>
            )}
            {isLoading && (
              <>
                <Typography>Creating new Wishlist Item...</Typography>
                <LinearProgress />
              </>
            )}
            {data && (
              <>
                <Typography variant="h4" gutterBottom id="wishlist-success-dialog-title">
                  Added to Wishlist!
                </Typography>
                <Typography variant="body1" gutterBottom id="wishlist-success-dialog-description">
                  Your custom swimming shorts configuration has been successfully added to your wishlist.
                </Typography>
              </>
            )}
          </Stack>
        </>
      }
      controls={
        <>
          <Stack direction="row" spacing={2}>
            <Button variant="text" onClick={handleBack} startIcon={<PhxIcon name="ArrowLeft" />}>
              Back to Design
            </Button>
          </Stack>
        </>
      }
    />
  )
}
